import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  arrayUnion,
  deleteField,
  limit
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { toast } from 'react-toastify';
import OrderHeader from '../components/orders/OrderHeader';
import OrderItems from '../components/orders/OrderItems';
import CancelOrderModal from '../components/orders/CancelOrderModal';
import { processOrderCancellation } from '../utils/walletTransactions';
import { handleOrderStatusChange } from '../services/stockManagement';
import { deleteWooCommerceOrder, updateWooCommerceOrderStatus } from '../services/woocommerce';
import type { Order } from '../types';
import { OrderTotalsWooCommerce } from '../components/orders/OrderTotalsWooCommerce';
import { OrderTotalsManual } from '../components/orders/OrderTotalsManual';
import { OrderTotalsMarketplace } from '../components/orders/OrderTotalsMarketplace';
import { OrderTotalsPrivateSale } from '../components/orders/OrderTotalsPrivateSale';

export const ORDER_STATUSES = {
  pending: 'Pendente',
  preparation: 'Em preparação',
  awaitingStock: 'Aguardar Stock',
  awaitingPickup: 'Aguardar recolha',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
  returned: 'Devolvido',
  awaiting_payment: 'Aguardando Pagamento'
} as const;

const DadosRecolha = ({ pedidoRecolha }: { pedidoRecolha: any }) => {
  if (!pedidoRecolha) return null;

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Em agendamento';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Em agendamento';
      return date.toLocaleDateString('pt-PT');
    } catch {
      return 'Em agendamento';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Dados da Recolha</h3>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          pedidoRecolha.estado === 'Agendado' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
        }`}>
          {pedidoRecolha.estado || 'Pendente'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-gray-600">Data do Pedido</p>
          <p className="font-medium">{formatDate(pedidoRecolha.dataPedido)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Data Prevista</p>
          <p className="font-medium">{formatDate(pedidoRecolha.dataRecolhaPrevista)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Número de Caixas</p>
          <p className="font-medium">{pedidoRecolha.numCaixas || '-'}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Peso Total</p>
          <p className="font-medium">{pedidoRecolha.pesoTotal ? `${pedidoRecolha.pesoTotal}kg` : '-'}</p>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-sm text-gray-600 mb-1">Morada de Recolha</p>
        <p className="font-medium">{pedidoRecolha.sellerCommercialName || pedidoRecolha.sellerLegalName || '-'}</p>
        <p>{pedidoRecolha.sellerAddress || '-'}</p>
        <p>{pedidoRecolha.sellerPostalCode} {pedidoRecolha.sellerCity}</p>
        <p>{pedidoRecolha.sellerDistrict || '-'}</p>
      </div>

      <div className="mt-4">
        <p className="text-sm text-gray-600 mb-1">Morada de Entrega</p>
        <p className="font-medium">{pedidoRecolha.buyerName || '-'}</p>
        <p>{pedidoRecolha.buyerAddress || '-'}</p>
        <p>{pedidoRecolha.buyerPostalCode} {pedidoRecolha.buyerCity}</p>
      </div>
    </div>
  );
};

export default function OrderDetails() {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [pedidoRecolha, setPedidoRecolha] = useState<any>(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [disputeUpdated, setDisputeUpdated] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commenting, setCommenting] = useState(false);
  const [disputeStatus, setDisputeStatus] = useState('');
  const [disputeNote, setDisputeNote] = useState('');
  const [fileAttachment, setFileAttachment] = useState<File | null>(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [integrations, setIntegrations] = useState<{ [key: string]: any }>({});
  const [productImages, setProductImages] = useState<{[key: string]: string}>({});
  const [numCaixas, setNumCaixas] = useState('');
  const [pesoTotal, setPesoTotal] = useState('');
  const { currentUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.companyId) {
      // Carregar integrações
      const integrationsQuery = query(
        collection(db, 'integrations'),
        where('companyId', '==', currentUser.companyId),
        where('type', '==', 'woocommerce')
      );

      const unsubscribe = onSnapshot(integrationsQuery, (snapshot) => {
        const integrationsMap: { [key: string]: any } = {};
        snapshot.forEach((doc) => {
          const integration = doc.data();
          integrationsMap[doc.id] = integration;
        });
        setIntegrations(integrationsMap);
      });

      return () => unsubscribe();
    }
  }, [currentUser]);

  useEffect(() => {
    if (id) {
      loadOrder();

      // Escutar mudanças nas solicitações de cancelamento em tempo real
      const q = query(
        collection(db, 'cancellation_requests'),
        where('orderId', '==', id),
        where('status', '==', 'pending'),
        limit(1)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        if (snapshot.size > 0) {
          // Se encontrar uma solicitação pendente, atualizar o pedido
          setOrder(prev => prev ? { ...prev, hasCancellationRequest: true } : null);
        } else {
          // Se não encontrar, garantir que hasCancellationRequest é false
          setOrder(prev => prev ? { ...prev, hasCancellationRequest: false } : null);
        }
      });

      return () => unsubscribe();
    }
  }, [id, disputeUpdated]);

  useEffect(() => {
    const fetchProductImages = async () => {
      if (!order) return;

      // If it's a WooCommerce order, images are in sourceData.line_items
      if (order.source === 'woocommerce' && order.sourceData?.line_items) {
        const wooImages: {[key: string]: string} = {};
        order.sourceData.line_items.forEach((item: any) => {
          if (item.image?.src) {
            wooImages[item.product_id.toString()] = item.image.src;
          }
        });
        setProductImages(wooImages);
        return;
      }

      // For manual orders or other sources, fetch images from Firebase products
      const items = getOrderItems();
      const productIds = items
        .filter(item => item.productId)
        .map(item => item.productId);

      if (productIds.length === 0) return;

      try {
        const productsRef = collection(db, 'products');
        const q = query(productsRef, where('id', 'in', productIds));
        const snapshot = await getDocs(q);
        
        const images: {[key: string]: string} = {};
        snapshot.forEach(doc => {
          const product = doc.data();
          // Check all possible image fields in order of priority
          const imageUrl = product.mainImage || product.image || product.images?.[0]?.src || product.baselinkerMainImage;
          if (imageUrl) {
            images[product.id] = imageUrl;
          }
        });

        setProductImages(images);
      } catch (error) {
        console.error('Error fetching product images:', error);
      }
    };

    fetchProductImages();
  }, [order]);

  useEffect(() => {
    if (id) {
      // Carregar recolha associada ao pedido
      const recolhasQuery = query(
        collection(db, 'recolhas'),
        where('orderId', '==', id),
        limit(1)
      );
      
      const unsubscribe = onSnapshot(recolhasQuery, (snapshot) => {
        if (!snapshot.empty) {
          const recolhaData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
          setPedidoRecolha(recolhaData);
        } else {
          setPedidoRecolha(null);
        }
      }, (error) => {
        console.error('Erro ao carregar recolha:', error);
        setPedidoRecolha(null);
      });

      return () => unsubscribe();
    }
  }, [id]);

  const loadOrder = () => {
    if (!id) return;

    try {
      // Set up real-time listener for the order
      const unsubscribe = onSnapshot(doc(db, 'orders', id), async (orderDoc) => {
        try {
          if (!orderDoc.exists()) {
            setError('Encomenda não encontrada');
            return;
          }

          const orderData = orderDoc.data();
          console.log('Raw Order Data:', orderData); // Debug log
          
          // Process WooCommerce order data
          if (orderData.source === 'woocommerce') {
            const processedOrder = {
              ...orderData,
              id: orderDoc.id,
              status: orderData.status || 'pending',
              shipping: {
                name: `${orderData.shipping?.first_name || ''} ${orderData.shipping?.last_name || ''}`.trim(),
                address_1: orderData.shipping?.address_1,
                address_2: orderData.shipping?.address_2,
                city: orderData.shipping?.city,
                postcode: orderData.shipping?.postcode,
                country: orderData.shipping?.country,
                phone: orderData.billing?.phone || orderData.shipping?.phone
              },
              items: orderData.sourceData?.line_items?.map((item: any) => ({
                name: item.name,
                quantity: item.quantity,
                price: parseFloat(item.price || '0'),
                total: parseFloat(item.total || '0'),
                image: item.image?.src,
                productId: item.product_id?.toString()
              })) || [],
              shippingMethod: orderData.shipping_lines?.[0]?.method_title || 'Normal',
              orderNumber: orderData.number || orderData.sourceOrderId,
              paymentStatus: orderData.date_paid ? 'paid' : 'pending'
            };
            console.log('Processed WooCommerce Order:', processedOrder); // Debug log
            setOrder(processedOrder);
          } else {
            // Load seller name if not loaded
            if (!orderData.sellerName && (orderData.sellerId || orderData.companyId)) {
              const companyDoc = await getDoc(doc(db, 'companies', orderData.sellerId || orderData.companyId));
              if (companyDoc.exists()) {
                orderData.sellerName = companyDoc.data().commercialName || companyDoc.data().legalName;
              }
            }

            setOrder({ id: orderDoc.id, ...orderData });
          }
          setError('');
        } catch (err) {
          console.error('Error processing order data:', err);
          setError('Erro ao processar dados da encomenda');
        } finally {
          setLoading(false);
        }
      }, (error) => {
        console.error('Error in real-time order subscription:', error);
        setError('Erro ao subscrever às atualizações da encomenda');
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up order listener:', err);
      setError('Erro ao configurar listener da encomenda');
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!order || !currentUser?.uid || !currentUser?.companyId) return;
    
    setUpdating(true);
    try {
      // Se for pedido WooCommerce, atualizar primeiro lá
      if (order.source === 'woocommerce') {
        try {
          // Buscar a integração do WooCommerce
          const integrationsRef = collection(db, 'integrations');
          const q = query(integrationsRef, 
            where('type', '==', 'woocommerce'),
            where('active', '==', true),
            where('companyId', '==', currentUser.companyId)
          );
          
          const integrationSnap = await getDocs(q);
          if (integrationSnap.empty) {
            throw new Error('Integração WooCommerce não encontrada');
          }
          
          const integrationId = integrationSnap.docs[0].id;
          await updateWooCommerceOrderStatus(integrationId, order.id, newStatus);
        } catch (wooError: any) {
          toast.error(wooError.message || 'Erro ao atualizar status no WooCommerce');
          setUpdating(false);
          return;
        }
      }
      
      // Atualizar no EMTUACASA
      await updateDoc(doc(db, 'orders', order.id), {
        status: newStatus,
        lastUpdated: new Date().toISOString(),
        statusHistory: arrayUnion({
          status: newStatus,
          date: new Date().toISOString(),
          userId: currentUser.uid
        })
      });
      
      // Atualizar stock se necessário
      await handleOrderStatusChange(order.id, newStatus, order.status, currentUser.companyId);
      
      toast.success('Status atualizado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao atualizar status:', error);
      toast.error(error.message || 'Erro ao atualizar status');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleDirectCancel = async () => {
    if (!order?.sourceOrderId || order.source !== 'woocommerce') return;
    
    if (window.confirm('Tem certeza que deseja apagar este pedido do WooCommerce? Ele será importado novamente na próxima sincronização.')) {
      try {
        setUpdating(true);
        await deleteWooCommerceOrder(order.sourceOrderId);
        navigate('/orders');
      } catch (err) {
        console.error('Erro ao apagar pedido:', err);
        setError('Erro ao apagar pedido');
      } finally {
        setUpdating(false);
      }
    }
  };

  const handleCancelOrder = async (reason: string) => {
    if (!order || !currentUser?.uid) return;

    setUpdating(true);
    setError('');

    try {
      const isManualOrder = !order.isPrivateSale && !order.isOnlineSale;
      
      // Process cancellation with wallet transactions
      const result = await processOrderCancellation(
        order.id,
        order.buyerCompanyId || order.buyerId,
        order.sellerId || order.companyId,
        order.total,
        order.subtotal,
        order.shippingCost,
        order.commission || 0,
        isManualOrder,
        reason === 'buyer_request'
      );

      if (!result.success) {
        throw new Error(result.error || 'Erro ao processar cancelamento');
      }

      // Restaurar o stock dos produtos
      if (['pending', 'preparation', 'awaitingStock', 'awaitingPickup'].includes(order.status)) {
        const stockResult = await handleOrderStatusChange(
          order.id,
          'cancelled',
          order.status,
          order.companyId || order.buyerCompanyId
        );

        if (!stockResult?.success) {
          console.error('Erro ao restaurar stock:', stockResult?.error);
          // Continue mesmo com erro de stock para não impedir o cancelamento
        }
      }

      // Update order status
      await updateDoc(doc(db, 'orders', order.id), {
        status: 'cancelled',
        updatedAt: new Date().toISOString(),
        comments: arrayUnion({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          text: `Encomenda cancelada. Motivo: ${reason}`,
          createdAt: new Date().toISOString(),
          createdBy: currentUser.displayName || currentUser.email || 'Usuário',
          createdById: currentUser.uid
        })
      });

      // Se for um pedido do WooCommerce, atualizar lá também
      if (order.source === 'woocommerce') {
        try {
          await updateWooCommerceOrderStatus(order.companyId, order.id, 'cancelled');
        } catch (wooError) {
          console.error('Erro ao atualizar status no WooCommerce:', wooError);
          setError('Erro ao atualizar status no WooCommerce. O status foi atualizado apenas no EMTUACASA.');
        }
      }

      // await loadOrder();
      setShowCancelModal(false);
    } catch (err) {
      console.error('Error cancelling order:', err);
      setError(err instanceof Error ? err.message : 'Erro ao cancelar encomenda');
      setUpdating(false);
    }
  };

  const handleDeleteWooCommerceOrder = async () => {
    if (!order?.sourceOrderId || order.source !== 'woocommerce') return;
    
    if (window.confirm('Tem certeza que deseja apagar este pedido do WooCommerce? Ele será importado novamente na próxima sincronização.')) {
      try {
        setUpdating(true);
        await deleteWooCommerceOrder(order.sourceOrderId);
        navigate('/orders');
      } catch (err) {
        console.error('Erro ao apagar pedido:', err);
        setError('Erro ao apagar pedido');
      } finally {
        setUpdating(false);
      }
    }
  };

  const handleShare = async () => {
    if (!order?.id) return;
    const shareUrl = `${window.location.origin}/public/order/${order.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copiado para a área de transferência!');
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Erro ao copiar o link');
    }
  };

  const handleInvoiceUploaded = () => {
    // Recarregar o pedido para atualizar o status
    loadOrder();
  };

  const handleInvoiceUpload = async (file: File) => {
    if (!file || !order) return;

    try {
      setUpdating(true);
      
      // Create a reference to the file in Firebase Storage
      const storage = getStorage();
      const fileRef = ref(storage, `invoices/${order.id}/${file.name}`);
      
      // Upload the file
      await uploadBytes(fileRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(fileRef);
      
      console.log('Download URL:', downloadURL);

      // Update order with invoice URL
      await updateDoc(doc(db, 'orders', order.id), {
        invoicePdf: downloadURL,
        invoiceStatus: 'invoiced',
        invoiceUploadedAt: new Date().toISOString()
      });

      // Refresh order data
      const updatedOrderDoc = await getDoc(doc(db, 'orders', order.id));
      if (updatedOrderDoc.exists()) {
        setOrder({ id: updatedOrderDoc.id, ...updatedOrderDoc.data() });
      }

      toast.success('Fatura carregada com sucesso!');
    } catch (error) {
      console.error('Error uploading invoice:', error);
      toast.error('Erro ao carregar a fatura. Por favor, tente novamente.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!order) return;

    try {
      setUpdating(true);
      const storage = getStorage();
      
      // Delete the file from storage if it exists
      if (order.invoicePdf) {
        const fileRef = ref(storage, order.invoicePdf);
        await deleteObject(fileRef).catch(err => console.log('Error deleting file:', err));
      }

      // Remove invoice data from the order
      await updateDoc(doc(db, 'orders', order.id), {
        invoicePdf: deleteField(),
        invoiceStatus: 'pending',
        invoiceUploadedAt: deleteField()
      });

      // Refresh order data
      const updatedOrderDoc = await getDoc(doc(db, 'orders', order.id));
      if (updatedOrderDoc.exists()) {
        setOrder({ id: updatedOrderDoc.id, ...updatedOrderDoc.data() });
      }

      toast.success('Fatura removida com sucesso!');
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Erro ao remover a fatura. Por favor, tente novamente.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDisputeStatusChange = (status: string) => {
    setDisputeStatus(status);
  };

  const handleFileAttachment = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) setFileAttachment(file);
    };
    input.click();
  };

  const handleSaveDispute = async () => {
    if (!order || !currentUser?.uid) return;

    setUpdating(true);
    try {
      await updateDoc(doc(db, 'orders', order.id), {
        dispute: {
          status: disputeStatus,
          note: disputeNote,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser.displayName || currentUser.email || 'Usuário',
          updatedById: currentUser.uid
        }
      });

      if (fileAttachment) {
        // Upload file to Firebase Storage
        const storage = getStorage();
        const storageRef = storage.ref();
        const fileRef = storageRef.child(`disputes/${order.id}.pdf`);
        await fileRef.put(fileAttachment);

        // Update order with dispute file URL
        await updateDoc(doc(db, 'orders', order.id), {
          dispute: {
            file: {
              url: await fileRef.getDownloadURL(),
              uploadedAt: new Date().toISOString()
            }
          }
        });
      }

      toast.success('Nota de disputa salva com sucesso!');
    } catch (err) {
      console.error('Error saving dispute:', err);
      toast.error('Erro ao salvar nota de disputa');
    } finally {
      setUpdating(false);
    }
  };

  const handleCommentSubmit = async () => {
    if (!order || !currentUser?.uid) return;

    setCommenting(true);

    try {
      await updateDoc(doc(db, 'orders', order.id), {
        comments: arrayUnion({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          text: newComment,
          createdAt: new Date().toISOString(),
          createdBy: currentUser.displayName || currentUser.email || 'Usuário',
          createdById: currentUser.uid
        })
      });

      setNewComment('');
    } catch (err) {
      console.error('Error submitting comment:', err);
    } finally {
      setCommenting(false);
    }
  };

  const getShippingInfo = () => {
    if (!order) return null;

    // Marketplace e Online Sales
    if (order.isOnlineSale || order.isMarketplace || order.source === 'marketplace') {
      return {
        name: order.shippingAddress?.name || '',
        street: order.shippingAddress?.street || '',
        complement: order.shippingAddress?.complement || '',
        number: order.shippingAddress?.number || '',
        city: order.shippingAddress?.city || '',
        district: order.shippingAddress?.district || '',
        postalCode: order.shippingAddress?.postalCode || '',
        country: order.shippingAddress?.country || '',
        phone: order.shippingAddress?.phone || '',
        email: order.shippingAddress?.email || '',
        taxNumber: order.shippingAddress?.taxNumber || ''
      };
    }

    // WooCommerce
    if (order.source === 'woocommerce' && order.sourceData?.shipping) {
      const shipping = order.sourceData.shipping;
      return {
        name: `${shipping.first_name} ${shipping.last_name}`.trim(),
        street: shipping.address_1,
        complement: shipping.address_2,
        number: '',
        city: shipping.city,
        district: shipping.state,
        postalCode: shipping.postcode,
        country: shipping.country === 'PT' ? 'Portugal' : shipping.country,
        phone: shipping.phone || order.sourceData?.billing?.phone || '',
        email: order.sourceData?.billing?.email || '',
        taxNumber: order.sourceData?.meta_data?.find(m => m.key === '_billing_nif')?.value || ''
      };
    }

    // Vendas Manuais
    if (order.isManual) {
      return {
        name: order.customer?.name || order.shippingAddress?.name || '',
        street: order.shippingAddress?.address || '',
        complement: '',
        number: '',
        city: order.shippingAddress?.city || '',
        district: '',
        postalCode: order.shippingAddress?.postalCode || '',
        country: order.shippingAddress?.country || '',
        phone: order.customer?.phone || order.shippingAddress?.phone || '',
        email: order.customer?.email || '',
        taxNumber: ''
      };
    }

    // Vendas Privadas
    if (order.isPrivateSale) {
      return {
        name: order.shippingAddress?.name || '',
        street: order.shippingAddress?.address || '',
        complement: '',
        number: '',
        city: order.shippingAddress?.city || '',
        district: order.shippingAddress?.district || '',
        postalCode: order.shippingAddress?.postalCode || '',
        country: order.shippingAddress?.country || '',
        phone: order.shippingAddress?.phone || '',
        email: order.shippingAddress?.email || '',
        taxNumber: ''
      };
    }

    return null;
  };

  const getProductImage = (item: any) => {
    // For manual orders, first check our fetched product images
    if (item.productId && productImages[item.productId]) {
      return productImages[item.productId];
    }

    // Try to get image from various sources
    if (item.imageUrl) return item.imageUrl;
    if (item.image?.src) return item.image.src;
    if (item.images?.[0]?.src) return item.images[0].src;
    if (item.product?.images?.[0]?.src) return item.product.images[0].src;
    if (typeof item.image === 'string') return item.image;
    if (item.mainImage) return item.mainImage;
    
    // For marketplace orders
    if (item.sourceData?.images?.[0]) return item.sourceData.images[0];
    if (item.sourceData?.image) return item.sourceData.image;
    
    return null;
  };

  const filterVendorProducts = (items: any[]) => {
    if (!currentUser || !items) return [];
    return items.filter(item => item.companyId === currentUser.companyId);
  };

  const getOrderItems = () => {
    if (!order) return [];

    // Para pedidos manuais, retorna os items com a imagem do produto
    if (order.source === 'manual') {
      return order.items.map(item => {
        // Log para debug
        console.log('Item original:', item);
        console.log('Item.product:', item.product);
        
        const processedItem = {
          ...item,
          mainImage: item.product?.mainImage || item.mainImage || item.product?.image || item.image,
          image: item.product?.image || item.image || item.product?.mainImage || item.mainImage,
          images: item.product?.images || item.images || []
        };

        // Log do item processado
        console.log('Item processado:', processedItem);
        return processedItem;
      });
    }

    // Para outros tipos de pedidos, retorna os items normalmente
    return order.items || [];
  };

  const calculateVendorTotals = (items: any[]) => {
    if (!items) return { subtotal: 0, total: 0 };
    
    // For manual orders and private sales, use the order totals directly
    if (order.isPrivateSale || (!order.isPrivateSale && !order.isOnlineSale)) {
      return {
        subtotal: order.subtotal || 0,
        total: order.total || 0
      };
    }
    
    // For marketplace orders, calculate from filtered items
    const vendorItems = filterVendorProducts(items);
    const subtotal = vendorItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const total = order.isOnlineSale ? subtotal : subtotal + (order.shippingCost || 0);

    return { subtotal, total };
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    
    // If timestamp is a Firestore Timestamp, convert to Date
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    
    // Format date to Portuguese locale
    return date.toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number | string | undefined | null): string => {
    // Convert to number if it's a string or handle undefined/null
    const numValue = typeof value === 'string' ? parseFloat(value) : Number(value || 0);
    
    // Check if it's a valid number
    if (isNaN(numValue)) return '€0.00';
    
    return `€${numValue.toFixed(2)}`;
  };

  const getOrderTypeTitle = (order: Order) => {
    if (order.source === 'woocommerce') {
      const integration = integrations[order.integrationId];
      return `Encomenda WOOCOMMERCE (${integration?.name || order.type || 'Loja'})`;
    }
    if (order.isOnlineSale) {
      return 'Encomenda MARKETPLACE';
    }
    if (order.isPrivateSale) {
      return 'Encomenda VENDA PRIVADA';
    }
    return 'Encomenda MANUAL';
  };

  const getOrderTypeDisplay = (order: any) => {
    if (order.source === 'woocommerce') {
      return { type: 'WooCommerce', style: 'bg-orange-100 text-orange-800' };
    }
    if (order.isOnlineSale || order.isMarketplace || order.source === 'marketplace') {
      return { type: 'Marketplace', style: 'bg-blue-100 text-blue-800' };
    }
    if (order.isPrivateSale) {
      return { type: 'Venda Privada', style: 'bg-purple-100 text-purple-800' };
    }
    if (order.isManual) {
      return { type: 'Manual', style: 'bg-gray-100 text-gray-800' };
    }
    return { type: 'Manual', style: 'bg-gray-100 text-gray-800' };
  };

  const renderShippingSection = () => {
    const shippingInfo = getShippingInfo();
    if (!shippingInfo) return null;

    return (
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium mb-4">Dados de Envio</h3>
        {order?.isGuestOrder && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-700">Cliente comprou sem registar</p>
          </div>
        )}
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-1">Nome</p>
            <p className="font-medium">{shippingInfo.name || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Rua</p>
            <p className="font-medium">{shippingInfo.street || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Complemento</p>
            <p className="font-medium">{shippingInfo.complement || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Número</p>
            <p className="font-medium">{shippingInfo.number || '-'}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Código Postal</p>
              <p className="font-medium">{shippingInfo.postalCode || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Cidade</p>
              <p className="font-medium">{shippingInfo.city || '-'}</p>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Distrito</p>
            <p className="font-medium">{shippingInfo.district || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">País</p>
            <p className="font-medium">{shippingInfo.country || '-'}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Telefone</p>
              <p className="font-medium">{shippingInfo.phone || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Email</p>
              <p className="font-medium">{shippingInfo.email || '-'}</p>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">NIF</p>
            <p className="font-medium">{shippingInfo.taxNumber || '-'}</p>
          </div>
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 italic">
              Estes dados devem ser usados apenas para envio através da transportadora
            </p>
          </div>
        </div>
      </div>
    );
  };

  const requisitarRecolha = async () => {
    if (!order || !numCaixas || !pesoTotal || !currentUser?.uid) {
      toast.error('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    try {
      // Buscar dados do utilizador logado
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        throw new Error('Dados do utilizador não encontrados');
      }
      const userData = userDoc.data();
      const userCompanyId = userData.companyId;

      if (!userCompanyId) {
        throw new Error('ID da empresa não encontrado para o utilizador');
      }

      // Buscar dados da empresa do utilizador logado
      const companyDoc = await getDoc(doc(db, 'companies', userCompanyId));
      if (!companyDoc.exists()) {
        throw new Error('Dados da empresa não encontrados');
      }
      const companyData = companyDoc.data();

      // Criar documento de recolha com valores padrão para campos obrigatórios
      const recolhaRef = doc(collection(db, 'recolhas'));
      const recolhaData = {
        id: recolhaRef.id,
        orderId: id || '',
        orderNumber: order.orderNumber || '',
        dataPedido: new Date().toISOString(),
        dataSolicitacao: new Date().toISOString(),
        dataRecolhaPrevista: '',
        estado: 'pendente',
        numCaixas: Number(numCaixas),
        pesoTotal: Number(pesoTotal),
        
        // Dados do vendedor (da empresa do utilizador logado)
        sellerId: userCompanyId,
        sellerName: companyData.commercialName || companyData.legalName || '',
        sellerCommercialName: companyData.commercialName || '',
        sellerLegalName: companyData.legalName || '',
        sellerAddress: companyData.address || '',
        sellerCity: companyData.city || 'Vila do Conde',
        sellerPostalCode: companyData.postalCode || '4485-704',
        sellerDistrict: companyData.district || 'Porto',
        sellerPhone: companyData.phone || '',
        sellerEmail: companyData.email || '',
        sellerVat: companyData.vat || '',
        
        // Dados do comprador (do pedido)
        buyerId: order.buyerId || '',
        buyerName: order.shippingAddress?.name || '',
        buyerAddress: order.shippingAddress?.street || '',
        buyerCity: order.shippingAddress?.city || '',
        buyerPostalCode: order.shippingAddress?.postalCode || '',
        buyerPhone: order.shippingAddress?.phone || '',
        buyerEmail: order.shippingAddress?.email || '',
        
        // Metadados
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      };

      // Salvar recolha no Firestore
      await setDoc(recolhaRef, recolhaData);
      
      // Atualizar status do pedido
      await updateDoc(doc(db, 'orders', id), {
        status: 'awaitingPickup',
        statusHistory: arrayUnion({
          status: 'awaitingPickup',
          date: new Date().toISOString(),
          userId: currentUser.uid,
          note: 'Recolha solicitada'
        })
      });

      // Enviar dados para webhook
      const webhookData = [{
        seller_vat: recolhaData.sellerVat,
        seller_commercial_name: recolhaData.sellerCommercialName,
        seller_commercial_email: recolhaData.sellerEmail,
        seller_commercial_phone: recolhaData.sellerPhone,
        seller_district: recolhaData.sellerDistrict,
        seller_legal_name: recolhaData.sellerLegalName,
        seller_phone: recolhaData.sellerPhone,
        seller_address: recolhaData.sellerAddress,
        seller_city: recolhaData.sellerCity,
        seller_postal_code: recolhaData.sellerPostalCode,
        
        buyer_name: recolhaData.buyerName,
        buyer_address: recolhaData.buyerAddress,
        buyer_city: recolhaData.buyerCity,
        buyer_postal_code: recolhaData.buyerPostalCode,
        buyer_phone: recolhaData.buyerPhone,
        buyer_email: recolhaData.buyerEmail,
        
        order_number: recolhaData.orderNumber,
        order_date: recolhaData.dataPedido,
        order_total: order.total || 0,
        order_items: order.items?.map(item => ({
          name: item.name || '',
          quantity: item.quantity || 0,
          price: item.price || 0,
          productId: item.productId || '',
          image: item.image || ''
        })) || [],
        
        num_boxes: recolhaData.numCaixas,
        total_weight: recolhaData.pesoTotal
      }];

      await fetch('https://hook.eu1.make.com/bi2kzsai4cf6wpqttd5ksafm14e7skgn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      // Fechar modal e limpar campos
      setNumCaixas('');
      setPesoTotal('');
      toast.success('Pedido de recolha criado com sucesso!');

    } catch (error) {
      console.error('Erro ao requisitar recolha:', error);
      toast.error('Erro ao criar pedido de recolha. Por favor, tente novamente.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center text-red-600">
            {error || 'Encomenda não encontrada'}
          </div>
        </div>
      </div>
    );
  }

  const orderTotals = calculateVendorTotals(order.items);

  return (
    <div>
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-between items-center mb-6">
            <OrderHeader
              order={order}
              status={order.status}
              onUpdateStatus={handleStatusChange}
              onCancel={() => setShowCancelModal(true)}
              updating={updating}
              buyerName={order.customer?.name}
              sellerName={order.seller?.name}
              paymentMethod={order.paymentMethod}
              isOnlineSale={order.isOnlineSale}
              isManualOrder={!order.isOnlineSale && !order.isPrivateSale}
            />
          </div>

          {error && (
            <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                {/* Informação de compra como convidado */}
                {order.isGuestOrder && order.isMarketplace && (
                  <div className="bg-blue-50 text-blue-700 px-4 py-3 rounded-lg mb-4">
                    Cliente comprou sem registar
                  </div>
                )}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-lg font-medium">Encomenda #{order.orderNumber}</span>
                </div>
                
                {/* Products Table */}
                <OrderItems 
                  items={getOrderItems()} 
                  shippingCost={order.shippingCost}
                  isManualOrder={order.source === 'manual'}
                />
                
                {/* Order Totals */}
                {order.source === 'woocommerce' && <OrderTotalsWooCommerce order={order} />}
                {order.source === 'manual' && <OrderTotalsManual order={order} />}
                {order.isMarketplace && <OrderTotalsMarketplace order={order} />}
                {order.isPrivateSale && <OrderTotalsPrivateSale order={order} />}

                {/* Invoice Upload Section */}
                <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm text-gray-600">Total da Venda: {order.source === 'woocommerce' ? formatCurrency(parseFloat(order.sourceData?.total || '0')) : formatCurrency(order.total)}</span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total da Venda: {order.source === 'woocommerce' ? formatCurrency(parseFloat(order.sourceData?.total || '0')) : formatCurrency(order.total)}</span>
                  </div>
                </div>

              </div>

              {/* Invoice Modal */}
              {showInvoiceModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                      <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>

                    <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                      <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-medium">Fatura da Venda</h3>
                          <button
                            onClick={() => setShowInvoiceModal(false)}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <object
                          data={order.invoicePdf}
                          type="application/pdf"
                          className="w-full h-[calc(100vh-200px)]"
                        >
                          <p>O seu navegador não consegue exibir o PDF. 
                            <a 
                              href={order.invoicePdf} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 ml-1"
                            >
                              Clique aqui para baixar
                            </a>
                          </p>
                        </object>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pickup Modal */}
              {showPickupModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                      <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>

                    <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                      <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-medium">Marcar Recolha</h3>
                          <button
                            onClick={() => setShowPickupModal(false)}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                          <svg className="h-6 w-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M13 16V6a1 1 0 11-2 0 1 1 0 012 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 13h8M13 11v8" />
                          </svg>
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                          <h3 className="text-lg leading-6 font-medium text-gray-900">
                            Brevemente
                          </h3>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500">
                              Esta funcionalidade estará disponível em breve.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="mt-5 sm:mt-6">
                        <button
                          type="button"
                          className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={() => setShowPickupModal(false)}
                        >
                          Fechar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Dispute Management - Only visible to admins */}
              {currentUser?.role === 'admin' && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="flex items-center gap-2 text-lg font-medium">
                      <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L1 21H23L12 2ZM12 6L19.53 19H4.47L12 6ZM11 10V14H13V10H11ZM11 16V18H13V16H11Z" />
                      </svg>
                      Gestão de Reclamação
                    </h3>
                    <select
                      value={disputeStatus}
                      onChange={(e) => handleDisputeStatusChange(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Sem Ação</option>
                      <option value="pending">Pendente</option>
                      <option value="resolved">Resolvido</option>
                      <option value="rejected">Rejeitado</option>
                    </select>
                  </div>

                  <div className="space-y-4">
                    <textarea
                      value={disputeNote}
                      onChange={(e) => setDisputeNote(e.target.value)}
                      placeholder="Adicionar nota interna..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      rows={4}
                    />

                    <div className="flex justify-between items-center">
                      <button
                        onClick={handleFileAttachment}
                        className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-.867 12.142A2 2 0 102-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5a2 2 0 012-2h3v2a1 1 0 001 1v3M4 7h16" />
                        </svg>
                        Anexar Arquivo
                      </button>

                      <button
                        onClick={handleSaveDispute}
                        className="px-4 py-2 bg-[#E3EBD6] text-gray-700 rounded-md hover:bg-[#d5e0c3]"
                      >
                        Salvar Nota
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="flex items-center gap-2 text-lg font-medium mb-4">
                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Comentários
                </h3>

                <div className="space-y-4">
                  {order.comments?.map((comment, index) => (
                    <div key={comment.id || `comment-${index}-${comment.createdAt}`} className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium">{comment.createdBy}</span>
                        <span className="text-sm text-gray-500">
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-gray-700">{comment.text}</p>
                    </div>
                  ))}

                  <form onSubmit={(e) => {
                    e.preventDefault();
                    handleCommentSubmit();
                  }} className="flex gap-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Adicione um comentário..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                    />
                    <button
                      type="submit"
                      disabled={!newComment.trim() || commenting}
                      className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
                    >
                      {commenting ? 'Enviando...' : 'Enviar'}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-4">O vendedor irá enviar para:</h3>
                {order && renderShippingSection()}
              </div>

              {/* Invoice Upload Section */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="flex items-center gap-2 text-lg font-medium">
                    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h3v2a1 1 0 001 1v3M4 7h16" />
                    </svg>
                    Fatura da Venda
                  </h3>
                  {order.invoiceStatus === 'invoiced' && (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      FATURADO
                    </span>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total da Venda: {order.source === 'woocommerce' ? formatCurrency(parseFloat(order.sourceData?.total || '0')) : formatCurrency(order.total)}</span>
                  </div>
                  
                  {order.invoiceStatus === 'invoiced' ? (
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          console.log('Opening invoice:', order.invoicePdf);
                          setShowInvoiceModal(true);
                        }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Ver Fatura
                      </button>
                      <button
                        onClick={handleDeleteInvoice}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 text-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 102-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5a2 2 0 012-2h3v2a1 1 0 001 1v3M4 7h16" />
                        </svg>
                        Remover Fatura
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary cursor-pointer">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="mt-2 text-sm text-gray-600">Clique para fazer upload da fatura</span>
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleInvoiceUpload(file);
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Dados da Recolha */}
              {pedidoRecolha && (
                <DadosRecolha pedidoRecolha={pedidoRecolha} />
              )}
            </div>
          </div>

          {showCancelModal && (
            <CancelOrderModal
              isOpen={showCancelModal}
              onClose={() => setShowCancelModal(false)}
              onConfirm={handleCancelOrder}
              loading={updating}
              orderTotal={order?.total || 0}
              orderSubtotal={order?.subtotal || 0}
              shippingCost={order?.shippingCost || 0}
              commission={order?.commission || 0}
              buyerId={order?.buyerId || ''}
              sellerId={order?.sellerId || order.companyId || ''}
              isOnlineSale={order?.isOnlineSale}
              isPrivateSale={order?.isPrivateSale}
              isManualOrder={!order?.isPrivateSale && !order?.isOnlineSale}
              order={order}
              isSeller={currentUser?.uid === order?.sellerId}
            />
          )}
        </div>
      </div>
    </div>
  );
}